import { Slot } from '@radix-ui/react-slot';
import { cn } from '../../../lib/utils';
import { type CharlieButtonProps, charlieButtonVariants } from './index.js';
import { CharlieSpinner } from '../spinner/charlie-spinner.js';

/**
 * CharlieButton — React equivalent of Rocket's `RocketButton`.
 *
 * Built on top of the [ShadCN Button](https://ui.shadcn.com/docs/components/button)
 * pattern (Radix Slot + CVA) and mirrors the API of the Vue-based
 * [`RocketButton`](libs/ui/rocket/src/components/ui/button/Button.vue).
 *
 * ## Variants
 *
 * Same set as RocketButton: `default`, `destructive`, `outline`, `secondary`,
 * `ghost`, `link` — plus `pill` and `block` modifiers.
 *
 * ## Usage
 *
 * ```tsx
 * import { CharlieButton } from '@ver.id/libs.ui.charlie';
 *
 * // Basic usage
 * <CharlieButton variant="default">Save</CharlieButton>
 *
 * // With loading state
 * <CharlieButton loading>Submitting...</CharlieButton>
 *
 * // Pill + block (full width, rounded)
 * <CharlieButton variant="secondary" pill block>
 *   Continue
 * </CharlieButton>
 *
 * // As a link (using asChild to render a different element)
 * <CharlieButton asChild variant="link">
 *   <a href="/dashboard">Go to dashboard</a>
 * </CharlieButton>
 *
 * // With label prop (convenience — same as wrapping text in children)
 * <CharlieButton label="Click me" variant="outline" />
 * ```
 *
 * ## Gotchas
 *
 * - **CSS import required**: Consumers must import the Charlie stylesheet for
 *   the theme variables to take effect:
 *   ```ts
 *   import '@ver.id/libs.ui.charlie/style.css';
 *   ```
 *   Alternatively, import from the barrel (`@ver.id/libs.ui.charlie`) which
 *   includes the CSS side-effect import.
 */
export function CharlieButton({
  className,
  variant,
  size,
  pill,
  block,
  loading = false,
  disabled = false,
  asChild = false,
  label,
  children,
  ...props
}: CharlieButtonProps) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(
        charlieButtonVariants({ variant, size, pill, block }),
        loading && 'pointer-events-none',
        disabled && 'pointer-events-none opacity-80',
        className,
      )}
      disabled={disabled}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading && <CharlieSpinner className="size-4" />}
          {children ?? (label && <span>{label}</span>)}
        </>
      )}
    </Comp>
  );
}
